import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { environment } from '../../../environments/environment';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTabsModule } from '@angular/material/tabs';
import { MatChipsModule } from '@angular/material/chips';
import { MatDialogModule, MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSnackBarModule, MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import { FormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { DrinksService } from '../../api/services/drinks.service';
import { OrderService } from '../../api/services/order.service';
import { CustomerService } from '../../api/services/customer.service';
import { Drink } from '../../api/models/drink.model';
import { Order } from '../../api/models/order.model';
import { Category } from '../../api/models/category.model';
import { Observable, forkJoin } from 'rxjs';
import { map, finalize } from 'rxjs/operators';
import { HeaderNavComponent } from "src/app/ui/header-nav/header-nav.component";
import { FooterComponent } from "src/app/ui/footer/footer.component";
import { LoadingSpinnerComponent } from '../../ui/loading-spinner/loading-spinner.component';
import { MatProgressSpinner } from "@angular/material/progress-spinner";

// Local interface for Cart Item to keep track of UI details
interface LocalCartItem {
  drink: Drink;
  quantity: number;
  notes?: string;
}

export type OrderType = 'table' | 'takeaway';
// MOBILE matches the backend's PaymentMethod.MOBILE. Bars are asked for a
// mobileMoneyAccount when they register, but customers had no way to pay
// into it — Card and Cash were the only options offered.
export type PaymentMethod = 'CARD' | 'CASH' | 'MOBILE' | 'DIGITAL';

@Component({
  selector: 'app-customer-app',
  standalone: true,
  imports: [
    CommonModule, MatCardModule, MatButtonModule, MatIconModule, MatTabsModule,
    MatChipsModule, MatDialogModule, MatFormFieldModule, MatInputModule,
    MatSnackBarModule, FormsModule,
    HeaderNavComponent,
    FooterComponent,
    LoadingSpinnerComponent,
    MatProgressSpinner
],
  templateUrl: './customer-app.component.html',
  styles: []
})
export class CustomerAppComponent implements OnInit, OnDestroy {
  drinks$: Observable<Drink[]>;
  categories: Category[] = []; // Fetched categories
  selectedCategoryIndex = 0;
  cart: LocalCartItem[] = [];
  
  isLoading = true; // Add loading state

  // Dialog states
  isQRScannerOpen = false;
  isDrinkDetailOpen = false;
  isCartOpen = false;
  isPaymentOpen = false;
  isPaymentConfirmationOpen = false;
  
  // Form data
  tableNumber = '';

  // Post-order rating prompt. Feedback was only reachable from a logged-in
  // customer's dashboard, via a past visit nobody was ever pointed at — so
  // ratings stayed empty, and the anonymous customers who make up most of the
  // traffic could never leave one at all. The session endpoint is public, so
  // asking here works whether or not they have an account.
  isFeedbackOpen = false;
  feedbackRating = 0;
  feedbackComment = '';
  private feedbackSessionId: string | null = null;
  customerName = '';
  itemNotes = '';
  orderNotes = '';
  orderType: OrderType = 'table';
  paymentMethod: PaymentMethod = 'CARD';
  selectedDrink: Drink | null = null;
  
  // Enhanced cart features
  invoiceNumber = '';
  isScanningQR = false;
  sessionId: string | null = null;

  constructor(
    private drinksService: DrinksService,
    private orderService: OrderService,
    private customerService: CustomerService,
    private snackBar: MatSnackBar,
    private router: Router,
    private route: ActivatedRoute,
    private dialog: MatDialog
  ) {
    this.drinks$ = this.drinksService.getDrinks();
  }

  /**
   * Bar id extracted from the QR-scan query string (?bar=BAR-XXX&table=1).
   * When present, the menu is loaded via the anonymous
   * /drinks/available?barId=... endpoint so customers who haven't logged in
   * as staff can still browse a bar's menu.
   */
  barIdFromQuery: string | null = null;

  ngOnDestroy(): void {
    // A component torn down mid-scan would otherwise leave the camera on.
    this.stopScanning();
  }

  ngOnInit(): void {
    this.generateInvoiceNumber();
    // Pick up bar + table from the QR-scan URL if present
    this.barIdFromQuery = this.route.snapshot.queryParamMap.get('bar');
    const t = this.route.snapshot.queryParamMap.get('table');
    if (t) this.tableNumber = t;
    this.loadData();
  }

  loadData(): void {
    this.isLoading = true;
    const drinks$ = this.barIdFromQuery
      ? this.drinksService.getAvailableMenu(this.barIdFromQuery)
      : this.drinksService.getDrinks();
    forkJoin({
      drinks: drinks$,
      categories: this.drinksService.getCategories()
    }).pipe(
      finalize(() => this.isLoading = false)
    ).subscribe({
      next: ({ drinks, categories }) => {
        this.categories = categories;
        // Enrich drinks with category names if missing
        this.drinks$ = new Observable(observer => {
          const enrichedDrinks = drinks.map(drink => {
            if (!drink.categoryName) {
              const category = categories.find(c => c.id === drink.categoryId);
              if (category) {
                drink.categoryName = category.name;
              }
            }
            return drink;
          });
          observer.next(enrichedDrinks);
          observer.complete();
        });
      },
      error: (err) => console.error('Error loading data:', err)
    });
  }

  generateInvoiceNumber(): void {
    const timestamp = Date.now().toString().slice(-6);
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    this.invoiceNumber = `INV-${timestamp}-${random}`;
  }

  onBack(): void {
    this.router.navigate(['/']);
  }

  onCategoryChange(event: any): void {
    this.selectedCategoryIndex = event.index;
  }

  getFilteredMenu(allDrinks: Drink[] | null): Drink[] {
    if (!allDrinks) return [];
    
    // Index 0 is 'All'
    if (this.selectedCategoryIndex === 0) {
      return allDrinks;
    }
    
    // Adjust index for categories array (which doesn't contain 'All')
    const category = this.categories[this.selectedCategoryIndex - 1];
    if (!category) return [];

    return allDrinks.filter(drink => {
      // Try matching by ID first (more reliable)
      if (drink.categoryId !== undefined && drink.categoryId === category.id) {
        return true;
      }
      // Fallback to name match
      if (drink.categoryName === category.name) {
        return true;
      }
      return false;
    });
  }

  openQRScanner(): void {
    this.isQRScannerOpen = true;
    this.requestCameraAccess();
  }

  /**
   * Opens the live-chat surface pre-wired to talk to the current bar.
   * The customer must be signed in — the livechat component redirects to
   * /login if the JWT is missing.
   */
  openBarChat(): void {
    if (!this.barIdFromQuery) return;
    this.router.navigate(['/livechat'], { queryParams: { bar: this.barIdFromQuery } });
  }

  /** The live preview the decoder reads frames from. */
  @ViewChild('qrVideo') qrVideo?: ElementRef<HTMLVideoElement>;
  private qrStream: MediaStream | null = null;
  private qrScanTimer: any = null;

  async requestCameraAccess(): Promise<void> {
    // Decoding happens here for real. The previous implementation opened the
    // camera, waited three seconds and then called simulateQRScan(), which
    // picked a random table between 1 and 20 and never looked at the image —
    // so it could not know which bar it was pointed at, let alone which table.
    const AnyWindow = window as any;
    if (!('BarcodeDetector' in AnyWindow)) {
      this.toast(
        'This browser cannot decode QR codes. Use your phone camera app to scan the code on your table.',
        'Close', { duration: 7000 });
      this.isQRScannerOpen = false;
      return;
    }

    try {
      this.qrStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' }
      });
      this.isScanningQR = true;
      setTimeout(() => this.startDecoding(), 0);
    } catch (error) {
      if (!environment.production) console.error('Camera access denied:', error);
      this.toast('Camera access denied. Please allow camera access to scan QR codes.',
                         'Close', { duration: 5000 });
      this.isQRScannerOpen = false;
    }
  }

  private async startDecoding(): Promise<void> {
    const video = this.qrVideo?.nativeElement;
    if (!video || !this.qrStream) return;
    video.srcObject = this.qrStream;
    await video.play().catch(() => {});

    const detector = new (window as any).BarcodeDetector({ formats: ['qr_code'] });
    this.qrScanTimer = setInterval(async () => {
      if (!this.isScanningQR) return;
      try {
        const codes = await detector.detect(video);
        if (codes?.length) this.onQrDecoded(String(codes[0].rawValue || ''));
      } catch {
        // A frame that cannot be decoded is normal; keep looking.
      }
    }, 400);
  }

  /**
   * A scanned code carries the bar and the table:
   *   https://<host>/customer?bar=BAR-XXXX&table=7
   * Reading both is what lets the customer order without typing anything.
   */
  private onQrDecoded(raw: string): void {
    let barId: string | null = null;
    let table: string | null = null;
    try {
      const url = new URL(raw, window.location.origin);
      barId = url.searchParams.get('bar');
      table = url.searchParams.get('table');
    } catch {
      this.toast('That code is not a table code for this platform.', 'Close', { duration: 4000 });
      return;
    }
    if (!barId) {
      this.toast('That code does not identify a bar.', 'Close', { duration: 4000 });
      return;
    }

    this.stopScanning();
    if (table) {
      this.tableNumber = table;
      this.orderType = 'table';
    }
    this.toast(
      table ? `Scanned — you are at table ${table}` : 'Scanned', 'Close', { duration: 3000 });

    // Reload the menu for the scanned bar. Same URL the phone's own camera app
    // would have opened, so both routes land in exactly the same place.
    if (barId !== this.barIdFromQuery) {
      this.router.navigate(['/customer'], {
        queryParams: table ? { bar: barId, table } : { bar: barId },
      }).then(() => window.location.reload());
    }
  }

  stopScanning(): void {
    this.isScanningQR = false;
    this.isQRScannerOpen = false;
    if (this.qrScanTimer) { clearInterval(this.qrScanTimer); this.qrScanTimer = null; }
    this.qrStream?.getTracks().forEach(t => t.stop());
    this.qrStream = null;
  }


  openDrinkDetail(drink: Drink): void {
    this.selectedDrink = drink;
    this.isDrinkDetailOpen = true;
  }

  addToCart(drink: Drink, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    
    if (!environment.production) {
      console.log('➕ Adding to cart:', drink.name);
    }
    
    const existingItem = this.cart.find(item => item.drink.id === drink.id && !item.notes);
    
    if (existingItem) {
      existingItem.quantity += 1;
      if (!environment.production) {
        console.log('➕ Updated existing item quantity:', existingItem.quantity);
      }
    } else {
      this.cart.push({ drink, quantity: 1 });
      if (!environment.production) {
        console.log('➕ Added new item to cart');
      }
    }
    
    this.toast(`${drink.name} added to cart`, 'Close', { duration: 3000 });
  }

  addToCartWithNotes(drink: Drink, notes: string): void {
    const existingItem = this.cart.find(item => item.drink.id === drink.id && item.notes === notes);
    
    if (existingItem) {
      existingItem.quantity += 1;
    } else {
      this.cart.push({ drink, quantity: 1, notes: notes || undefined });
    }
    
    this.toast(`${drink.name} added to cart`, 'Close', { duration: 3000 });
    this.isDrinkDetailOpen = false;
    this.itemNotes = '';
  }

  updateCartQuantity(item: LocalCartItem, change: number): void {
    const newQuantity = item.quantity + change;
    
    if (newQuantity <= 0) {
      this.cart = this.cart.filter(cartItem => cartItem !== item);
    } else {
      item.quantity = newQuantity;
    }
  }

  getTotalPrice(): number {
    return this.cart.reduce((total, item) => total + (item.drink.price * item.quantity), 0);
  }

  getTotalItems(): number {
    return this.cart.reduce((total, item) => total + item.quantity, 0);
  }

  /**
   * Show a message at the TOP of the screen.
   *
   * Material puts a snack bar at the bottom by default, which is exactly where
   * the persistent "View cart" bar now sits. The toast covered it, and for the
   * seconds after adding an item — the very moment a customer reaches for the
   * cart — a tap on that bar landed on the toast and did nothing. Measured:
   * elementFromPoint at the bar's centre returned mat-mdc-snack-bar-label.
   */
  private toast(message: string, action: string = 'Close', config: MatSnackBarConfig = {}): void {
    this.snackBar.open(message, action, { verticalPosition: 'top', ...config });
  }

  openCart(): void {
    this.isCartOpen = true;
  }

  setOrderType(type: OrderType): void {
    this.orderType = type;
    if (type === 'takeaway') {
      this.tableNumber = '';
    }
  }

  setPaymentMethod(method: PaymentMethod): void {
    this.paymentMethod = method;
  }

  placeOrder(): void {
    if (this.orderType === 'table' && !this.tableNumber) {
      this.toast('Please enter your table number or scan the QR code', 'Close', { duration: 3000 });
      return;
    }
    
    if (this.cart.length === 0) {
      this.toast('Your cart is empty', 'Close', { duration: 3000 });
      return;
    }

    // Handle Takeaway case
    const effectiveTableNumber = this.orderType === 'takeaway' ? 'TAKEAWAY' : this.tableNumber;

    // Real bar id: prefer the QR-scan query param; fall back to the id on
    // the drinks in the cart so at least the correct bar is targeted.
    const bar = this.barIdFromQuery
              || this.cart[0]?.drink?.barId
              || null;
    if (!bar) {
      this.toast('Bar id unknown — please scan the table QR code first.', 'Close', { duration: 4000 });
      return;
    }

    // Create session before proceeding to payment
    this.customerService.createSession(effectiveTableNumber, bar).subscribe({
      next: (session) => {
        if (!environment.production) console.log('Session created:', session);
        this.sessionId = session.sessionId;
        this.isCartOpen = false;
        this.isPaymentOpen = true;
      },
      error: (err) => {
        if (!environment.production) console.error('Error creating session:', err);
        this.toast('Unable to start ordering session. Please try again.', 'Close', { duration: 3000 });
      }
    });
  }

  processPayment(): void {
    // Show confirmation dialog before processing payment
    this.isPaymentOpen = false;
    this.isPaymentConfirmationOpen = true;
  }

  confirmPayment(): void {
    this.isPaymentConfirmationOpen = false;
    // One path for every method. Card used to branch into a stub that faked a
    // Stripe round-trip with two timers and then announced "Payment
    // successful!" — while the backend had only recorded a PENDING intent.
    // completeOrder() places the order and calls the payment service with the
    // method the customer actually chose; the response decides what happens next.
    this.completeOrder();
  }

  cancelPaymentConfirmation(): void {
    this.isPaymentConfirmationOpen = false;
    this.isPaymentOpen = true;
  }

  completeOrder(): void {
    if (!this.sessionId) {
      this.toast('Session expired. Please try again.', 'Close', { duration: 3000 });
      return;
    }
    // Push every cart item into the session cart via the anonymous
    // /customer/sessions/{id}/cart endpoint, then place the order, then pay.
    // We chain sequentially so item add order matches what the user built.
    const sid = this.sessionId;
    const addAll = this.cart.reduce<Promise<void>>((chain, item) =>
      chain.then(() => new Promise<void>((resolve, reject) => {
        this.customerService.addToCart(sid, item.drink.id, item.quantity,
                                       item.notes).subscribe({
          next: () => resolve(),
          error: (e) => reject(e),
        });
      })), Promise.resolve());

    addAll
      .then(() => new Promise<any>((resolve, reject) => {
        this.customerService.placeOrder(sid).subscribe({
          next: (r) => resolve(r), error: (e) => reject(e),
        });
      }))
      .then((placed) => {
        if (!environment.production) console.log('Order placed:', placed);
        return new Promise<any>((resolve, reject) => {
          this.customerService.initiatePayment(sid, this.paymentMethod).subscribe({
            next: (r) => resolve(r), error: (e) => reject(e),
          });
        });
      })
      .then((payment: any) => {
        // A card payment comes back as an intent with somewhere to send the
        // customer. Announcing success here — as the old code did for every
        // method — told them their card had been charged when nothing had been
        // authorised yet.
        const redirectUrl = payment?.redirectUrl;
        if (redirectUrl) {
          this.toast('Redirecting to the payment provider…', 'Close', { duration: 3000 });
          window.location.href = redirectUrl;
          return;
        }
        this.toast('Order placed & payment recorded!', 'Close', { duration: 4000 });
        // Ask before clearing: resetOrder() drops the session id the feedback
        // endpoint is keyed on.
        this.feedbackSessionId = this.sessionId;
        this.isFeedbackOpen = true;
        this.resetOrder();
      })
      .catch((err) => {
        if (!environment.production) console.error('completeOrder failed:', err);
        this.toast('Failed to complete order. Please try again.', 'Close', { duration: 4000 });
      });
  }


  setFeedbackRating(stars: number): void {
    this.feedbackRating = stars;
  }

  submitFeedback(): void {
    if (!this.feedbackSessionId || this.feedbackRating < 1) {
      this.toast('Pick a star rating first.', 'Close', { duration: 3000 });
      return;
    }
    this.customerService.submitFeedback(this.feedbackSessionId, {
      overallRating: this.feedbackRating,
      comments: this.feedbackComment || '',
      customerName: this.customerName || undefined,
      type: 'GENERAL',
    }).subscribe({
      next: () => {
        this.toast('Thank you — your rating helps other customers.', 'Close', { duration: 4000 });
        this.dismissFeedback();
      },
      error: () => {
        this.toast('Could not send your rating. Please try again.', 'Close', { duration: 4000 });
      },
    });
  }

  dismissFeedback(): void {
    this.isFeedbackOpen = false;
    this.feedbackRating = 0;
    this.feedbackComment = '';
    this.feedbackSessionId = null;
  }

  resetOrder(): void {
    this.cart = [];
    this.isPaymentOpen = false;
    this.orderNotes = '';
    this.tableNumber = '';
    this.customerName = '';
    this.sessionId = null;
    this.generateInvoiceNumber(); // Generate new invoice number for next order
  }

  closePayment(): void {
    this.isPaymentOpen = false;
    this.isPaymentConfirmationOpen = false;
    this.isCartOpen = true;
  }
}